import { parse, type ParseResult } from 'mrz';

const MRZ_WHITELIST = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<';

const toMrzCharset = (value: string) => value.toUpperCase().replace(/[^A-Z0-9<]/g, '');

const padLine = (line: string, length: number) => {
  if (line.length === length) {
    return line;
  }
  if (line.length > length) {
    return line.slice(0, length);
  }
  return line.padEnd(length, '<');
};

const REQUIRED_CHECK_DIGIT_FIELDS = [
  'documentNumberCheckDigit',
  'birthDateCheckDigit',
  'expirationDateCheckDigit'
] as const;

const hasValidCheckDigits = (result: ParseResult, requireComposite = true) => {
  const fields = requireComposite
    ? [...REQUIRED_CHECK_DIGIT_FIELDS, 'compositeCheckDigit']
    : REQUIRED_CHECK_DIGIT_FIELDS;
  const checks = result.details.filter(
    (detail) => detail.field && (fields as readonly string[]).includes(detail.field)
  );
  return checks.length > 0 && checks.every((detail) => detail.valid);
};

export const hasValidMrzFirstRow = (result: ParseResult): boolean =>
  result.format === 'TD3' &&
  !!result.fields.documentCode?.startsWith('P') &&
  ['documentCode', 'issuingState', 'lastName', 'firstName'].every((field) =>
    result.details.some((detail) => detail.field === field && detail.valid)
  );

const tryParseTd3 = (lines: string[], requireComposite = true): ParseResult | null => {
  try {
    const result = parse(lines, { autocorrect: true });
    if (result.format !== 'TD3') {
      return null;
    }
    const documentNumber = result.documentNumber || result.fields.documentNumber;
    if (!documentNumber && !result.fields.lastName) {
      return null;
    }
    if (!hasValidCheckDigits(result, requireComposite)) {
      return null;
    }
    return result;
  } catch {
    return null;
  }
};

export const MRZ_FRAME = {
  insetX: 0.04,
  insetBottom: 0.04,
  height: 0.36
} as const;

const mrzFrameBand = {
  top: 1 - MRZ_FRAME.insetBottom - MRZ_FRAME.height,
  height: MRZ_FRAME.height
};

const MIN_LINE_LENGTH = 30;

// OCR regularly picks up a stray character before the row, so the tail is worth trying
// alongside the padded row: both keep the 44 columns aligned when only one of them is right.
const rowVariants = (line: string): string[] =>
  line.length > 44 ? [padLine(line, 44), line.slice(-44)] : [padLine(line, 44)];

// The leading `P<` sits at the very edge of the page and is often dropped, which shifts
// every name on the row. Restoring it only makes sense when the row also came up two
// columns short, otherwise the first characters were misread rather than lost.
const firstRowVariants = (line: string): string[] =>
  line.startsWith('P') || line.length > 42 ? rowVariants(line) : [...rowVariants(line), `P<${padLine(line, 42)}`];

const candidatePairs = (ocrText: string): string[][] => {
  const pairs: string[][] = [];

  const lines = ocrText
    .toUpperCase()
    .split(/\n+/)
    .map((line) => toMrzCharset(line))
    .filter((line) => line.length >= MIN_LINE_LENGTH);

  lines.forEach((line, index) => {
    if (line.length >= 80) {
      pairs.push([padLine(line.slice(0, 44), 44), padLine(line.slice(44), 44)]);
    }
    const next = lines[index + 1];
    if (!next) {
      return;
    }
    firstRowVariants(line).forEach((first) => {
      rowVariants(next).forEach((second) => {
        pairs.push([first, second]);
      });
    });
  });

  // Printed words such as PASSPORT sit above the MRZ and also match, so every
  // occurrence has to be tried instead of only the first one.
  const compact = toMrzCharset(ocrText);
  const starts = /P[A-Z<]/g;
  let match = starts.exec(compact);
  while (match) {
    if (compact.length - match.index >= 88) {
      pairs.push([compact.slice(match.index, match.index + 44), compact.slice(match.index + 44, match.index + 88)]);
    }
    match = starts.exec(compact);
  }

  return pairs;
};

export const extractMrzLines = (ocrText: string): string[] | null => {
  const candidates = candidatePairs(ocrText).flatMap((lines) => {
    const result = tryParseTd3(lines, false);
    if (!result) {
      return [];
    }
    const alignment = hasValidMrzFirstRow(result)
      ? 2
      : Number(/^P[A-Z<](?:[A-Z]{3}|D<<)[A-Z<]{39}$/.test(lines[0]));
    return [{ lines, alignment, composite: Number(hasValidCheckDigits(result, true)) }];
  });

  candidates.sort((first, second) => second.composite - first.composite || second.alignment - first.alignment);
  return candidates[0]?.lines || null;
};

type BandLayout = { top: number; height: number };

// The MRZ sits at the bottom of the data page, but how much of the frame it takes
// depends on how close the passport is held, so each attempt sweeps a different band.
// Every band has to stay tall enough to hold both rows: cropping close to the first
// row makes Tesseract return it alone, and a single row never validates.
const BAND_LAYOUTS: readonly BandLayout[] = [
  { top: 0.55, height: 0.45 },
  { top: 0.62, height: 0.38 },
  { top: 0.48, height: 0.52 },
  { top: 0, height: 1 }
];

const STILL_BAND_LAYOUTS: readonly BandLayout[] = [
  { top: 0.55, height: 0.45 },
  { top: 0.62, height: 0.38 },
  { top: 0.48, height: 0.52 },
  mrzFrameBand,
  { top: 0, height: 1 }
];

const OCR_MIN_WIDTH = 1400;
const MAX_SOURCE_SIDE = 2500;

const cropBand = (
  source: HTMLCanvasElement,
  layout: BandLayout,
  contrast = 1.45
): HTMLCanvasElement => {
  const insetX = Math.round(source.width * MRZ_FRAME.insetX);
  const top = Math.round(source.height * layout.top);
  const width = Math.max(1, source.width - insetX * 2);
  const height = Math.max(1, Math.min(Math.round(source.height * layout.height), source.height - top));
  const scale = Math.max(1, Math.ceil(OCR_MIN_WIDTH / width));
  const cropped = document.createElement('canvas');
  cropped.width = width * scale;
  cropped.height = height * scale;
  const context = cropped.getContext('2d');
  if (!context) {
    return source;
  }
  context.filter = `grayscale(1) contrast(${contrast})`;
  context.drawImage(source, insetX, top, width, height, 0, 0, cropped.width, cropped.height);
  return cropped;
};

const otsuThreshold = (histogram: number[], total: number): number => {
  let sum = 0;
  for (let level = 0; level < 256; level += 1) {
    sum += level * histogram[level];
  }

  let sumBackground = 0;
  let weightBackground = 0;
  let bestVariance = 0;
  let threshold = 127;

  for (let level = 0; level < 256; level += 1) {
    weightBackground += histogram[level];
    if (weightBackground === 0) {
      continue;
    }
    const weightForeground = total - weightBackground;
    if (weightForeground === 0) {
      break;
    }
    sumBackground += level * histogram[level];
    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (sum - sumBackground) / weightForeground;
    const variance = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;
    if (variance > bestVariance) {
      bestVariance = variance;
      threshold = level;
    }
  }

  return threshold;
};

const binarize = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return source;
  }

  context.drawImage(source, 0, 0);
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = image;
  const gray = new Uint8ClampedArray(data.length / 4);
  const histogram = new Array<number>(256).fill(0);

  for (let i = 0, pixel = 0; i < data.length; i += 4, pixel += 1) {
    const level = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) | 0;
    gray[pixel] = level;
    histogram[level] += 1;
  }

  const threshold = otsuThreshold(histogram, gray.length);

  for (let i = 0, pixel = 0; i < data.length; i += 4, pixel += 1) {
    const level = gray[pixel] > threshold ? 255 : 0;
    data[i] = level;
    data[i + 1] = level;
    data[i + 2] = level;
    data[i + 3] = 255;
  }

  context.putImageData(image, 0, 0);
  return canvas;
};

const discardCanvas = (canvas: HTMLCanvasElement) => {
  canvas.width = 0;
  canvas.height = 0;
};

type TesseractWorker = {
  setParameters: (params: Record<string, string>) => Promise<unknown>;
  recognize: (image: HTMLCanvasElement) => Promise<{ data: { text: string } }>;
  terminate: () => Promise<unknown>;
};

export type ReadMrzMode = 'live' | 'still';

const scaleToCanvas = (width: number, height: number, draw: (canvas: HTMLCanvasElement) => void): HTMLCanvasElement => {
  const scale = Math.min(1, MAX_SOURCE_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  draw(canvas);
  return canvas;
};

export const canvasFromBlob = async (blob: Blob): Promise<HTMLCanvasElement> => {
  if (typeof createImageBitmap === 'function') {
    try {
      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
      } catch {
        bitmap = await createImageBitmap(blob);
      }

      try {
        return scaleToCanvas(bitmap.width, bitmap.height, (canvas) => {
          const context = canvas.getContext('2d');
          if (!context) {
            throw new Error('CANVAS_UNAVAILABLE');
          }
          context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        });
      } finally {
        bitmap.close();
      }
    } catch {
      // Fall through to HTMLImageElement when createImageBitmap cannot decode the file.
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('IMAGE_LOAD_FAILED'));
      element.src = url;
    });
    return scaleToCanvas(image.naturalWidth, image.naturalHeight, (canvas) => {
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('CANVAS_UNAVAILABLE');
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const createMrzOcrWorker = async (): Promise<TesseractWorker> => {
  const { createWorker, PSM } = await import('tesseract.js');
  // The stock English model reads the MRZ filler `<` as C, L or K, which breaks the
  // check digits on every frame. This OCR-B model is trained on MRZ and is served
  // from public/tessdata so the scan does not depend on an external CDN.
  const worker = await createWorker('mrz', 1, {
    langPath: `${process.env.PUBLIC_URL || ''}/tessdata`,
    gzip: false,
    logger: () => undefined
  });
  await worker.setParameters({
    tessedit_char_whitelist: MRZ_WHITELIST,
    tessedit_pageseg_mode: PSM.SINGLE_BLOCK,
    user_defined_dpi: '300'
  });
  return worker;
};

const parseRecognizedText = (text: string): ParseResult | null => {
  const lines = extractMrzLines(text || '');
  return lines ? tryParseTd3(lines, true) || tryParseTd3(lines, false) : null;
};

export const readMrzFromCanvas = async (
  source: HTMLCanvasElement,
  worker: TesseractWorker,
  attempt = 0,
  options?: { mode?: ReadMrzMode }
): Promise<ParseResult> => {
  const mode = options?.mode ?? 'live';
  const layouts = mode === 'still' ? STILL_BAND_LAYOUTS : [BAND_LAYOUTS[attempt % BAND_LAYOUTS.length]];
  const contrast = mode === 'still' ? 1.7 : 1.45;
  // The LSTM engine sometimes reads the MRZ better with the charset restriction
  // and sometimes worse, so live scans alternate it instead of betting on one.
  const restrictCharset = mode === 'still' ? true : Math.floor(attempt / BAND_LAYOUTS.length) % 2 === 0;
  const created: HTMLCanvasElement[] = [];

  await worker.setParameters({ tessedit_char_whitelist: restrictCharset ? MRZ_WHITELIST : '' });

  try {
    for (const layout of layouts) {
      const band = cropBand(source, layout, contrast);
      if (band !== source) {
        created.push(band);
      }
      const binary = binarize(band);
      if (binary !== band && binary !== source) {
        created.push(binary);
      }

      for (const image of [band, binary]) {
        try {
          const { data } = await worker.recognize(image);
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[mrz] attempt', attempt, mode, 'charset', restrictCharset, JSON.stringify(data.text));
          }
          const result = parseRecognizedText(data.text || '');
          if (result) {
            return result;
          }
        } catch {
          continue;
        }
      }
    }
    throw new Error('MRZ_INVALID');
  } finally {
    created.forEach(discardCanvas);
    discardCanvas(source);
  }
};
